import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Which tab a page is on, kept in its address — `?tab=transcript`,
 * `?side=mine`, `?range=7d` — so a reload, a shared link and Back all come
 * back to it. Vladimir, 2026-09-25: *"add selected tabs as url parameters on
 * all pages so reload or go back does not clear selected tab"*.
 *
 * ⚠ **Replaced, never pushed.** Back leaves the page rather than stepping
 * through every tab clicked on it — and the page it goes back to still shows
 * its tab, because the tab is in the address Back returns to.
 */
export function useUrlParam(name: string): [string | null, (next: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const set = useCallback(
    (next: string | null) =>
      setParams(
        (was) => {
          const now = new URLSearchParams(was);
          if (next === null) now.delete(name);
          else now.set(name, next);
          return now;
        },
        { replace: true, preventScrollReset: true },
      ),
    [name, setParams],
  );
  return [params.get(name), set];
}

/**
 * One of a known set of tabs. An address naming a tab the page does not have
 * (an old link, a typo) shows the page's own first tab rather than nothing.
 *
 * `fallback` is where the page lands with no tab in its address. Choosing it
 * takes the parameter away again — the address stays clean — unless the
 * landing depends on something that changes (the meeting page lands on Your
 * notes while it is being recorded and on Summary after): then a choice is
 * written down even when it is the landing, so a reload keeps it.
 */
export function useTab<K extends string>(
  name: string,
  keys: readonly K[],
  fallback: K,
  opts: { keepFallback?: boolean } = {},
): [K, (next: K) => void] {
  const [raw, setRaw] = useUrlParam(name);
  const value = raw !== null && (keys as readonly string[]).includes(raw) ? (raw as K) : fallback;
  const keep = opts.keepFallback === true;
  const set = useCallback(
    (next: K) => setRaw(next === fallback && !keep ? null : next),
    [fallback, keep, setRaw],
  );
  return [value, set];
}

/**
 * A filter somebody types, in the address as well (2026-09-25: "Add filters
 * to the url params as well") — written a moment after the typing stops, so a
 * keystroke is never a navigation, and read back when the address changes
 * under it: Back, or a link that carries a filter.
 *
 * ⚠ The box shows its own state, not the address: an address updated per
 * keystroke lands a render late and the caret jumps to the end.
 */
export function useUrlText(name: string, delay = 300): [string, (next: string) => void] {
  const [raw, setRaw] = useUrlParam(name);
  const [text, setText] = useState(raw ?? '');
  const written = useRef(raw ?? '');
  useEffect(() => {
    const now = raw ?? '';
    if (now === written.current) return;
    written.current = now;
    setText(now);
  }, [raw]);
  useEffect(() => {
    if (text === written.current) return;
    const timer = window.setTimeout(() => {
      written.current = text;
      setRaw(text === '' ? null : text);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [text, delay, setRaw]);
  return [text, setText];
}
